import { useEffect, useMemo, useState } from 'react';
import { Search, Trash2, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../admin/api';

const STATUSES = ['New', 'Contacted', 'Qualified', 'Quoted', 'Won', 'Lost'];
const STATUS_STYLES = {
  New: 'bg-bronze/10 text-bronze',
  Contacted: 'bg-blue-900/10 text-blue-900',
  Qualified: 'bg-amber-700/10 text-amber-800',
  Quoted: 'bg-purple-900/10 text-purple-900',
  Won: 'bg-green-800/10 text-green-800',
  Lost: 'bg-red-800/10 text-red-800',
};

const LeadsPage = () => {
  const [leads, setLeads] = useState([]);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [source, setSource] = useState('');
  const [days, setDays] = useState('');
  const [sortAsc, setSortAsc] = useState(false);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    if (status) params.set('status', status);
    if (source) params.set('source', source);
    if (days) params.set('days', days);
    const { data } = await api.get(`/admin/leads?${params}`);
    setLeads(data);
  };

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, status, source, days]);

  const sorted = useMemo(() => [...leads].sort((a, b) =>
    sortAsc ? a.created_at.localeCompare(b.created_at) : b.created_at.localeCompare(a.created_at)
  ), [leads, sortAsc]);

  const counts = useMemo(() => leads.reduce((acc, l) => ({ ...acc, [l.status]: (acc[l.status] || 0) + 1 }), {}), [leads]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/leads/${selected.id}`, { status: selected.status, notes: selected.notes });
      setLeads((ls) => ls.map((l) => (l.id === data.id ? data : l)));
      setSelected(data);
      toast.success('Lead updated');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete lead "${selected.name}"? This cannot be undone.`)) return;
    await api.delete(`/admin/leads/${selected.id}`);
    setLeads((ls) => ls.filter((l) => l.id !== selected.id));
    setSelected(null);
    toast.success('Lead deleted');
  };

  return (
    <div data-testid="admin-leads" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="font-serif text-3xl font-medium text-ink">Leads</h1>
        <div className="flex flex-wrap gap-2">
          {STATUSES.map((s) => (
            <span key={s} data-testid={`lead-stat-${s.toLowerCase()}`} className={`px-3 py-1.5 text-xs font-semibold ${STATUS_STYLES[s]}`}>
              {s} · {counts[s] || 0}
            </span>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative min-w-[240px] flex-1">
          <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted2" />
          <input data-testid="leads-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, phone, city…" className="input-field !pl-10" />
        </div>
        <select data-testid="leads-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className="input-field !w-auto">
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select data-testid="leads-source-filter" value={source} onChange={(e) => setSource(e.target.value)} className="input-field !w-auto">
          <option value="">All sources</option>
          <option value="Quote Form">Quote Form</option>
          <option value="Contact Form">Contact Form</option>
        </select>
        <select data-testid="leads-date-filter" value={days} onChange={(e) => setDays(e.target.value)} className="input-field !w-auto">
          <option value="">All time</option>
          <option value="7">Last 7 days</option>
          <option value="28">Last 28 days</option>
        </select>
        <button data-testid="leads-sort" onClick={() => setSortAsc(!sortAsc)} className="btn-outline !px-4 !py-3">
          Date {sortAsc ? '↑' : '↓'}
        </button>
      </div>

      <div className="overflow-x-auto border border-line bg-paper">
        <table className="w-full min-w-[860px] text-left text-sm">
          <thead>
            <tr className="border-b border-line text-[10px] font-semibold uppercase tracking-[0.2em] text-muted2">
              <th className="px-5 py-3.5">Name</th>
              <th className="px-5 py-3.5">Contact</th>
              <th className="px-5 py-3.5">City</th>
              <th className="px-5 py-3.5">Project</th>
              <th className="px-5 py-3.5">Source</th>
              <th className="px-5 py-3.5">Date</th>
              <th className="px-5 py-3.5">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {sorted.map((l) => (
              <tr key={l.id} data-testid={`lead-row-${l.id.slice(0, 8)}`} onClick={() => setSelected({ ...l })} className="cursor-pointer transition-colors hover:bg-paper2/50">
                <td className="px-5 py-3.5 font-medium text-ink">{l.name}</td>
                <td className="px-5 py-3.5 text-muted2"><span className="block">{l.email}</span><span className="text-xs">{l.phone}</span></td>
                <td className="px-5 py-3.5 text-muted2">{l.city || '—'}</td>
                <td className="max-w-[200px] truncate px-5 py-3.5 text-muted2">{l.project_type}</td>
                <td className="px-5 py-3.5 text-muted2">{l.source}</td>
                <td className="px-5 py-3.5 text-muted2">{new Date(l.created_at).toLocaleDateString()}</td>
                <td className="px-5 py-3.5"><span className={`px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider ${STATUS_STYLES[l.status] || ''}`}>{l.status}</span></td>
              </tr>
            ))}
            {sorted.length === 0 && (
              <tr><td colSpan={7} className="px-5 py-10 text-center text-muted2">No leads match your filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 flex justify-end bg-graphite/40" onClick={() => setSelected(null)}>
          <div data-testid="lead-detail" className="h-full w-full max-w-md overflow-y-auto bg-paper p-8 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div>
                <h2 className="font-serif text-2xl font-medium text-ink">{selected.name}</h2>
                <p className="mt-1 text-xs text-muted2">{new Date(selected.created_at).toLocaleString()} · {selected.source}</p>
              </div>
              <button data-testid="lead-detail-close" onClick={() => setSelected(null)} aria-label="Close"><X className="h-5 w-5 text-muted2" /></button>
            </div>
            <dl className="mt-6 space-y-3 border-y border-line py-6 text-sm">
              {[['Email', selected.email], ['Phone', selected.phone], ['City', selected.city || '—'], ['Project type', selected.project_type], ['Message', selected.message || '—']].map(([k, v]) => (
                <div key={k} className="grid grid-cols-[110px_1fr] gap-3">
                  <dt className="text-xs font-semibold uppercase tracking-[0.15em] text-muted2">{k}</dt>
                  <dd className="text-ink/80">{v}</dd>
                </div>
              ))}
            </dl>
            <div className="mt-6">
              <label htmlFor="lead-status" className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.2em] text-muted2">Status</label>
              <select id="lead-status" data-testid="lead-status-select" value={selected.status} onChange={(e) => setSelected({ ...selected, status: e.target.value })} className="input-field">
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="mt-5">
              <label htmlFor="lead-notes" className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.2em] text-muted2">Notes</label>
              <textarea id="lead-notes" data-testid="lead-notes-textarea" rows={5} value={selected.notes || ''} onChange={(e) => setSelected({ ...selected, notes: e.target.value })} className="input-field resize-none" placeholder="Internal notes…" />
            </div>
            <div className="mt-6 flex gap-3">
              <button data-testid="lead-save-button" onClick={save} disabled={saving} className="btn-primary flex-1 disabled:opacity-50">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save Changes'}
              </button>
              <button data-testid="lead-delete-button" onClick={remove} className="inline-flex items-center gap-2 border border-red-800/30 px-5 text-sm font-semibold text-red-800 transition-colors hover:bg-red-800 hover:text-paper">
                <Trash2 className="h-4 w-4" /> Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadsPage;
